import React from 'react';
import { theme } from '../../styles/theme';
import { useWindowSize } from '../../hooks/useWindowSize';
import FilterPanel from '../FilterPanel/FilterPanel';
import ActiveFilters from '../FilterPanel/ActiveFilters';
import { FilterState } from '../../types/album';

interface MainLayoutProps {
  children: React.ReactNode;
  filters: FilterState;
  availableGenres: string[];
  availableArtists: string[];
  availableDecades: string[];
  availableCountries: Array<{ code: string; name: string; count: number }>;
  availableLanguages: Array<{ code: string; name: string; count: number }>;
  yearRange: [number, number];
  onFiltersChange: (filters: FilterState) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  albumCount: number;
  onShowSyncStatus: () => void;
  showSyncButton?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  filters,
  availableGenres,
  availableArtists,
  availableDecades,
  availableCountries,
  availableLanguages,
  yearRange,
  onFiltersChange,
  showFilters,
  onToggleFilters,
  albumCount,
  onShowSyncStatus,
  showSyncButton = true
}) => {
  const { isMobile, isTablet } = useWindowSize();
  
  const getFilterPanelWidth = () => {
    if (isMobile) return '100vw';
    if (isTablet) return '250px';
    return '300px';
  };
  
  const layoutStyle: React.CSSProperties = {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: (showFilters && !isMobile) ? getFilterPanelWidth() : '0',
    right: 0,
    height: '60px',
    background: theme.colors.surface,
    borderBottom: `1px solid ${theme.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    padding: `0 ${isMobile ? theme.spacing.md : theme.spacing.lg}`,
    zIndex: theme.zIndex.sticky,
    transition: `left ${theme.transitions.normal}`,
  };

  const toggleButtonStyle: React.CSSProperties = {
    background: theme.colors.primary,
    color: 'white',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '0.875rem',
    fontWeight: '500',
    marginRight: theme.spacing.sm,
  };

  const settingsButtonStyle: React.CSSProperties = {
    background: theme.colors.border,
    color: theme.colors.text,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '0.875rem',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: theme.colors.text,
    marginLeft: theme.spacing.md,
  };

  const hasActiveFilters = filters.searchText || 
                        filters.genres.length > 0 || 
                        filters.decades.length > 0 || 
                        filters.countries.length > 0 || 
                        filters.languages.length > 0 ||
                        filters.artists.length > 0 ||
                        (filters.yearRange[0] !== 1950 || filters.yearRange[1] !== new Date().getFullYear());
  
  const contentStyle: React.CSSProperties = {
    flex: 1,
    marginLeft: (showFilters && !isMobile) ? getFilterPanelWidth() : '0',
    transition: `margin-left ${theme.transitions.normal}`,
    overflow: 'auto',
    height: '100vh',
    paddingTop: hasActiveFilters ? '140px' : '60px',
    paddingBottom: theme.spacing.xl,
  };

  const activeFiltersStyle: React.CSSProperties = {
    position: 'fixed',
    top: '60px',
    left: (showFilters && !isMobile) ? getFilterPanelWidth() : '0',
    right: 0,
    zIndex: theme.zIndex.sticky - 1,
    transition: `left ${theme.transitions.normal}`,
  };

  const handleRemoveFilter = (type: 'genres' | 'decades' | 'countries' | 'languages' | 'artists', value: string) => {
    switch (type) {
      case 'genres':
        const newGenres = filters.genres.filter(g => g !== value);
        onFiltersChange({ ...filters, genres: newGenres });
        break;
      case 'decades':
        const newDecades = filters.decades.filter(d => d !== value);
        onFiltersChange({ ...filters, decades: newDecades });
        break;
      case 'countries':
        const newCountries = filters.countries.filter(c => c !== value);
        onFiltersChange({ ...filters, countries: newCountries });
        break;
      case 'languages':
        const newLanguages = filters.languages.filter(l => l !== value);
        onFiltersChange({ ...filters, languages: newLanguages });
        break;
      case 'artists':
        const newArtists = filters.artists.filter(a => a !== value);
        onFiltersChange({ ...filters, artists: newArtists });
        break;
    }
  };

  const handleClearSearch = () => {
    onFiltersChange({ ...filters, searchText: '' });
  };

  const handleToggleFilterMode = () => {
    onFiltersChange({ 
      ...filters, 
      filterMode: filters.filterMode === 'any' ? 'all' : 'any' 
    });
  };

  return (
    <div style={layoutStyle}>
      <FilterPanel
        filters={filters}
        availableGenres={availableGenres}
        availableArtists={availableArtists}
        availableDecades={availableDecades}
        availableCountries={availableCountries}
        availableLanguages={availableLanguages}
        yearRange={yearRange}
        onFiltersChange={onFiltersChange}
        isVisible={showFilters}
        onClose={isMobile ? onToggleFilters : undefined}
      />
      
      <div style={{flex: 1}}>
        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button style={toggleButtonStyle} onClick={onToggleFilters}>
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            {showSyncButton && (
              <button style={settingsButtonStyle} onClick={onShowSyncStatus}>
                Settings
              </button>
            )}
          </div>
          <h1 style={titleStyle}>Record Collection</h1>
        </header>
        
        <div style={activeFiltersStyle}>
          <ActiveFilters
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
            onClearSearch={handleClearSearch}
            onToggleFilterMode={handleToggleFilterMode}
            availableCountries={availableCountries}
            availableLanguages={availableLanguages}
          />
        </div>
        
        <main style={contentStyle} className="scrollbar-thin">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;